import * as React from 'react';
import { useTheme } from '@mui/material/styles';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import Menu from '@mui/material/Menu';
import Box from '@mui/material/Box';
import MenuItem from '@mui/material/MenuItem';

import { EventParticipationStatusEnum, useUpdateEventParticipationMutation } from '@graphql';
import { useToastError } from '@hooks';

interface ParticipationButtonProps {
  eventId: string;
  status?: EventParticipationStatusEnum | null;
}

export function ParticipationButton(props: Readonly<ParticipationButtonProps>) {
  const { eventId, status } = props;
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [menuWidth, setMenuWidth] = React.useState<number | null>(null);
  const buttonRef = React.useRef<HTMLButtonElement>(null);
  const open = Boolean(anchorEl);
  const toastError = useToastError();
  const { t } = useTranslation('event');

  const statusColor = {
    [EventParticipationStatusEnum.Invited]: 'uncolored',
    [EventParticipationStatusEnum.Confirmed]: 'success',
    [EventParticipationStatusEnum.Interested]: 'warning',
    [EventParticipationStatusEnum.Declined]: 'error',
    [EventParticipationStatusEnum.Canceled]: 'error',
    [EventParticipationStatusEnum.Removed]: 'error',
  };

  const statusLabel = t(`participant.status.${status ?? EventParticipationStatusEnum.Invited}`);

  const menuSx = {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '8px 16px',
    whiteSpace: 'normal',
  };

  const [updateStatus, { error }] = useUpdateEventParticipationMutation({
    awaitRefetchQueries: true,
    refetchQueries: ['GetEventDetailed', 'ListMyEvents'],
  });

  const handleTryUnfriend = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setMenuWidth(buttonRef.current ? buttonRef.current.offsetWidth : null);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleChangeStatus = async (event: React.MouseEvent<HTMLButtonElement>) => {
    const action = event.currentTarget.dataset.id as EventParticipationStatusEnum | undefined;

    if (!action) {
      return;
    }

    await updateStatus({
      variables: { eventId, status: action },
    });

    handleCloseMenu();
  };

  React.useEffect(() => {
    if (error) {
      toastError(error);
    }
  }, [error]);

  if (!status) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        fullWidth
        ref={buttonRef}
        variant={status === EventParticipationStatusEnum.Invited ? 'contained' : 'outlined'}
        color={statusColor[status] ?? 'uncolored'}
        onClick={handleTryUnfriend}
        endIcon={<theme.icons.expandMore />}
      >
        {statusLabel}
      </Button>
      <Menu
        id="basic-menu"
        anchorEl={anchorEl}
        elevation={3}
        open={open}
        sx={{ width: '100%', mt: 1 }}
        onClose={handleCloseMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          list: {
            'aria-labelledby': 'basic-button',
            style: { width: menuWidth ?? '100%' },
          },
        }}
      >
        <MenuItem
          divider
          data-id={EventParticipationStatusEnum.Confirmed}
          onClick={handleChangeStatus}
          selected={status === EventParticipationStatusEnum.Confirmed}
          sx={menuSx}
        >
          <theme.icons.check sx={{ color: theme.palette.success.main, mr: 1 }} />
          {t('participant.action.Confirm')}
        </MenuItem>
        <MenuItem
          divider
          data-id={EventParticipationStatusEnum.Interested}
          onClick={handleChangeStatus}
          selected={status === EventParticipationStatusEnum.Interested}
          sx={menuSx}
        >
          <theme.icons.close sx={{ color: theme.palette.warning.main, mr: 1 }} />
          {t('participant.action.Maybe')}
        </MenuItem>
        <MenuItem
          divider
          data-id={EventParticipationStatusEnum.Declined}
          onClick={handleChangeStatus}
          selected={status === EventParticipationStatusEnum.Declined}
          sx={menuSx}
        >
          <theme.icons.close sx={{ color: theme.palette.error.main, mr: 1 }} />
          {t('participant.action.Decline')}
        </MenuItem>
      </Menu>
    </Box>
  );
}
